/** Inclusive 6-digit numeric range for kids pickup codes. */
const MIN = 100_000;
const MAX = 999_999;

/**
 * Generate a 6-digit pickup code not present in `takenCodes`.
 *
 * Codes are family-shared and human-read, so a wide-but-short numeric space is
 * intentional. The caller passes the codes currently open in the roster so a
 * new family never reuses an in-use code; the DB trigger is the final
 * cross-household guard. Falls back to a linear scan only if the random space
 * is improbably saturated.
 */
export function generatePickupCode(takenCodes?: Iterable<string>): string {
  const taken = takenCodes instanceof Set ? takenCodes : new Set(takenCodes ?? []);
  for (let attempt = 0; attempt < 50; attempt++) {
    const code = String(Math.floor(MIN + Math.random() * (MAX - MIN + 1)));
    if (!taken.has(code)) return code;
  }
  for (let c = MIN; c <= MAX; c++) {
    const code = String(c);
    if (!taken.has(code)) return code;
  }
  // Roster impossibly full (900k+ open codes) — return a random code anyway.
  return String(Math.floor(MIN + Math.random() * (MAX - MIN + 1)));
}

/** True when a Supabase error is the family-aware pickup-code collision raise. */
export function isPickupCodeCollision(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { message?: string };
  return typeof e.message === "string" && e.message.includes("pickup_code_collision");
}

/**
 * True when a Supabase error is record_kids_check_in's serialization-failure
 * raise (errcode 40001) for a reservation whose room changed between the
 * lock-stabilize pass and the final post-lock recheck — a real but
 * extremely narrow race, not a data problem. Callers should retry once with
 * fresh state; a second attempt re-probes and re-locks against whatever
 * room is actually in effect by then.
 */
export function isRetryableCheckInRaceError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: string };
  return e.code === "40001";
}
